import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import FileIcon from 'components/ReusableComponents/FileIcon'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import {
  dripMediumOptionsWithEmail,
  dripMediumOptionsWithText
} from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import MainTemplate from 'features/CustomCampaigns/EditCampaign/MainTemplate'
import CampaignEmailCustomizationSection from 'features/EmailEditor/CampaignEmailCustomizationSection'
import CampaignEmailDesignTemplatePicker from 'features/EmailEditor/CampaignEmailDesignTemplatePicker'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import {
  getExtensionFromUrl,
  getImgUrlFromCDN,
  isCampaignImageVisible
} from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import { getRequiredTagIdsForCustomCampaign } from 'features/ManageCampaigns/utils/campaignTagConfig'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { convertFromHours, convertToHours } from '../dripSequenceHelpers'
import ConfigurationStep from './ConfigurationStep'
import { ReactComponent as CloseIcon } from './close.svg'
import styles from './EditDripStepDialog.module.scss'

const steps = {
  configuration: 'configuration',
  textContent: 'textContent',
  email: 'email'
}

const stepNames = {
  configuration: 'Configuration',
  textContent: 'Text content',
  email: 'Email'
}

const mediaTag = mediaTags.campaign

function EditDripStepDialogInner({
  title,
  onClose,
  onSave,
  onPersistDraft,
  isSaving,
  initialData,
  showConfigurationStep = true,
  contentId,
  campaignId,
  medium
}) {
  const availableSteps = showConfigurationStep
    ? Object.keys(steps)
    : Object.keys(steps).filter(step => step !== steps.configuration)

  const defaultStep = showConfigurationStep
    ? steps.configuration
    : dripMediumOptionsWithText.includes(medium)
      ? steps.textContent
      : steps.email
  const [activeStep, setActiveStep] = useState(defaultStep)

  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)
  const hotelId = useSelector(selectCurrentHotelId)
  const requiredTagIds = getRequiredTagIdsForCustomCampaign(hotelId)

  const [isEnabled, setIsEnabled] = useState(initialData?.isEnabled ?? true)
  const [sendAfterValue, setSendAfterValue] = useState(
    initialData?.sendAfterValue ??
      convertFromHours(initialData?.hoursAfterPreviousCampaign).displayValue
  )
  const [sendAfterUnit, setSendAfterUnit] = useState(
    initialData?.sendAfterUnit ??
      convertFromHours(initialData?.hoursAfterPreviousCampaign).unit
  )

  const [messageTemplate, setMessageTemplate] = useState(initialData?.messageTemplate ?? '')
  const [messageMedia, setMessageMedia] = useState(initialData?.messageMedia ?? null)
  const [usedAllTags, setUsedAllTags] = useState(true)

  const { saveChanges, hasUnsavedChanges, refreshEmailData } = useEmailEditor()

  const handleTextChange = text => {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  const buildPayload = () => ({
    isEnabled,
    sendAfterValue,
    sendAfterUnit,
    hoursAfterPreviousCampaign: convertToHours(sendAfterValue, sendAfterUnit),
    medium,
    messageTemplate,
    messageMedia,
    emailHtml: null,
    hasEmailCampaign: dripMediumOptionsWithEmail.includes(medium)
  })

  const handleSave = async () => {
    if (hasUnsavedChanges) {
      await saveChanges()
    }
    onSave?.(buildPayload())
  }

  const handlePersistDraftBeforeTest = onPersistDraft
    ? async () => {
        await onPersistDraft(buildPayload())
      }
    : null

  const visibleSteps = availableSteps.filter(step => {
    if (step === steps.textContent) {
      return dripMediumOptionsWithText.includes(medium)
    }
    if (step === steps.email) {
      return dripMediumOptionsWithEmail.includes(medium)
    }
    return true
  })

  return (
    <Dialog verticalPadding={30}>
      <div className={styles.resolutionWrapper}>
        <div className={styles.dialogWrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button className={styles.closeButton} onClick={onClose} type="button">
              <CloseIcon />
            </button>
            <div className={styles.tabs}>
              {visibleSteps.map(step => (
                <button
                  key={step}
                  onClick={() => setActiveStep(step)}
                  className={step === activeStep ? styles.activeTab : styles.tab}
                  type="button"
                >
                  {stepNames[step]}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.content}>
            {activeStep === steps.configuration && showConfigurationStep && (
              <ConfigurationStep
                isEnabled={isEnabled}
                setIsEnabled={setIsEnabled}
                sendAfterValue={sendAfterValue}
                setSendAfterValue={setSendAfterValue}
                sendAfterUnit={sendAfterUnit}
                setSendAfterUnit={setSendAfterUnit}
                medium={medium}
              />
            )}

            {activeStep === steps.textContent && dripMediumOptionsWithText.includes(medium) && (
              <div className={styles.textContentWrapper}>
                <MainTemplate
                  messageTemplate={messageTemplate}
                  onTextChange={handleTextChange}
                  usedAllTags={usedAllTags}
                  setUsedAllTags={setUsedAllTags}
                  setMessageMedia={setMessageMedia}
                  messageMedia={messageMedia}
                  isAllTagsRequired={true}
                  mediaTag={mediaTag}
                  requiredTagIds={requiredTagIds}
                  isMediaRequired={false}
                  customUrl={null}
                  setCustomUrl={() => {}}
                  isCustomUrlAllowed={false}
                  contentId={contentId}
                  onBeforeSendTestSms={handlePersistDraftBeforeTest}
                  requireEngagePlusForImage={false}
                />
              </div>
            )}

            {activeStep === steps.email && dripMediumOptionsWithEmail.includes(medium) && (
              <div className={styles.emailStepContent}>
                <CampaignEmailDesignTemplatePicker
                  contentId={contentId}
                  campaignDescription={title}
                  onTemplateApplied={() => refreshEmailData()}
                />
                <CampaignEmailCustomizationSection
                  includeInboxFields
                  className={styles.emailCustomizationPanel}
                />
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <SecondaryRegularButton onClick={onClose} disabled={isSaving}>
              Discard
            </SecondaryRegularButton>
            <PrimaryRegularButton onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save step'}
            </PrimaryRegularButton>
          </div>
        </div>

        <div
          className={
            activeStep === steps.email
              ? `${styles.previewHolder} ${styles.previewHolderEmail}`
              : styles.previewHolder
          }
        >          {activeStep !== steps.email && dripMediumOptionsWithText.includes(medium) && (
            <TextPreview>
              <>
                <TextPreviewBubble
                  defaultData={{ userName, hotelName }}
                  hasLink
                  text={messageTemplate}
                />
                {messageMedia && isCampaignImageVisible(messageMedia) && (
                  <img
                    src={getImgUrlFromCDN(messageMedia, mediaTag, hotelId)}
                    alt="campaign media"
                    className={styles.campaignMedia}
                  />
                )}
                {messageMedia && getExtensionFromUrl(messageMedia) === 'pdf' && (
                  <div className={styles.campaignFile}>
                    <FileIcon extension="pdf" />
                    <span />
                  </div>
                )}
              </>
            </TextPreview>
          )}
          {activeStep === steps.email && dripMediumOptionsWithEmail.includes(medium) && (
            <EmailPreview
              isEditable={false}
              holderClassName={styles.dripEmailPreviewHolder}
              testHolderClassName={styles.dripEmailTestHolder}
              skipInitialSectionReset
              hideEditContentButton
            >
              <TestEmail />
            </EmailPreview>
          )}
        </div>
      </div>
    </Dialog>
  )
}

function EditDripStepDialog({ contentId, campaignId, medium, onPersistDraft, ...props }) {
  return (
    <EmailEditorProvider contentId={contentId} contentCategory="Custom" mode="campaignBody">
      <EditDripStepDialogInner
        contentId={contentId}
        campaignId={campaignId}
        medium={medium}
        onPersistDraft={onPersistDraft}
        {...props}
      />
    </EmailEditorProvider>
  )
}

export default EditDripStepDialog
